import {
  SUPERNOVA_CHAIN_ID,
  SUPERNOVA_COLLECTION_ADDRESS,
  SUPERNOVA_STAKING_ADDRESS,
} from "./supernovaContracts";
import { SUPERNOVA_ERROR, SupernovaChainError } from "./supernovaChainError";

/**
 * supernovaWriteGuards — the assertions that stand between a member's choices
 * and their wallet's signature prompt.
 *
 * WHAT THIS FILE IS FOR. Every other layer is already careful: the modal builds
 * the request, supernovaWriteArgs maps the units, supernovaChain simulates
 * before it asks. This file assumes all of that could be wrong anyway, and
 * checks the finished transaction against constants that are compiled into the
 * build. It is the difference between "we believe we're sending a stake to the
 * staking contract" and "this transaction cannot reach any other address".
 *
 * THE THREE THINGS IT REFUSES TO LET THROUGH:
 *
 *  1. A WRONG TARGET. `to` must be strictly the staking contract for stake /
 *     unstake / emergencyUnstake, and strictly the collection for the ERC-1155
 *     approval. Not "one of our addresses" — the specific one for that call.
 *  2. A WRONG OPERATOR. setApprovalForAll is the single most dangerous thing a
 *     member signs here: it lets one contract move their whole collection. The
 *     operator must be the staking contract and the grant must be `true` for
 *     that exact operator — an approval pointed anywhere else is the shape every
 *     NFT-drainer phish takes, so it dies here rather than reaching a wallet.
 *  3. CALLDATA THAT DRIFTED FROM THE SCREEN. The member agreed to a quantity, a
 *     type and a lock. The arguments are re-derived independently and compared
 *     against the payload the flow described (and logged) — if the two disagree
 *     by so much as one digit, nobody is asked to sign anything.
 *
 * A GUARD FAILURE IS OUR BUG, NOT THE MEMBER'S. It carries GUARD_FAILED rather
 * than WRITE_FAILED, and it always throws BEFORE the wallet is touched, so the
 * honest answer on screen is "we stopped, nothing was submitted".
 *
 * PURE. No viem, no React, no network, no provider. Addresses are compared as
 * lowercased strings because checksum casing is presentation, not identity —
 * everything else is compared strictly.
 */

/** Member-safe copy for every guard failure. Deliberately identical: the member
    can do nothing about any of them, and the distinction is in the cause. */
const GUARD_MESSAGE =
  "We stopped this transaction before your wallet was asked to sign it, " +
  "because an internal safety check didn't pass. Nothing was submitted and " +
  "nothing changed on-chain. Please report this — it's a problem on our side.";

const guardFailure = (detail) =>
  new SupernovaChainError(
    SUPERNOVA_ERROR.GUARD_FAILED,
    GUARD_MESSAGE,
    new Error(`supernova write guard: ${detail}`)
  );

/** Address identity. Case-insensitive by design; type-strict on purpose. */
const isSameAddress = (a, b) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.trim() !== "" &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

/* ── 1. The target address ──────────────────────────────────────────────── */

/**
 * The `to` of a member-signed write must be one of exactly two constants, and
 * which one depends on the call. Anything else — including the other Supernova
 * address — is refused.
 *
 * @param {string} target the address the transaction would be sent to
 * @param {"staking"|"collection"} expect which constant it must equal
 * @param {string} what the call being guarded, for the cause message
 * @returns {string} the target, unchanged, when it passes
 * @throws {SupernovaChainError} GUARD_FAILED
 */
export function assertWriteTarget(target, expect, what) {
  const expected =
    expect === "staking"
      ? SUPERNOVA_STAKING_ADDRESS
      : expect === "collection"
        ? SUPERNOVA_COLLECTION_ADDRESS
        : null;

  if (expected === null) {
    throw guardFailure(`${what}: unknown target kind "${String(expect)}"`);
  }
  if (!isSameAddress(target, expected)) {
    throw guardFailure(
      `${what}: transaction target ${String(target)} is not the ${expect} ` +
        `contract ${expected}`
    );
  }
  return target;
}

/* ── 2. The approval operator ───────────────────────────────────────────── */

/**
 * setApprovalForAll(operator, approved). The operator must be the staking
 * contract and nothing else, and `approved` must be exactly the boolean `true`
 * — not a truthy value, because a coerced argument is how the wrong permission
 * gets granted quietly.
 *
 * @param {unknown} operator
 * @param {unknown} approved
 * @throws {SupernovaChainError} GUARD_FAILED
 */
export function assertApprovalOperator(operator, approved) {
  if (!isSameAddress(operator, SUPERNOVA_STAKING_ADDRESS)) {
    throw guardFailure(
      `setApprovalForAll: operator ${String(operator)} is not the staking ` +
        `contract ${SUPERNOVA_STAKING_ADDRESS}`
    );
  }
  if (approved !== true) {
    throw guardFailure(
      `setApprovalForAll: approved must be exactly true, got ${String(approved)}`
    );
  }
}

/* ── 3. Calldata vs the state that produced it ──────────────────────────── */

/* Contract arguments are BigInts; the payload carries them as decimal strings so
   it survives JSON.stringify. Compare in the payload's own units. */
const asDecimal = (value) => (value === null || value === undefined ? null : String(value));

const assertField = (what, field, actual, expected) => {
  if (actual !== expected) {
    throw guardFailure(
      `${what}: ${field} is ${String(actual)}, but the screen said ${String(expected)}`
    );
  }
};

/**
 * Shared preamble: the payload describes a transaction on the chain and contract
 * we expect, for the wallet that is about to sign it.
 */
function assertPayloadEnvelope(payload, request, expectedContract, what) {
  if (!payload || typeof payload !== "object") {
    throw guardFailure(`${what}: no payload to check the transaction against`);
  }
  assertField(what, "chainId", payload.chainId, SUPERNOVA_CHAIN_ID);

  if (!isSameAddress(payload.contract, expectedContract)) {
    throw guardFailure(
      `${what}: payload contract ${String(payload.contract)} is not ${expectedContract}`
    );
  }

  /* The signer. A payload built for one wallet must never be submitted from
     another — that is a signature over somebody else's position. */
  const wallet = request && request.wallet;
  if (!isSameAddress(payload.wallet, wallet)) {
    throw guardFailure(
      `${what}: payload wallet ${String(payload.wallet)} is not the requesting ` +
        `wallet ${String(wallet)}`
    );
  }
}

/**
 * A stake, checked three ways: the envelope, the member's choices as they were
 * shown, and the four contract arguments re-derived from the request.
 *
 * `args` is what supernovaChain is about to put on the wire, as BigInts.
 *
 * @param {Object} payload  from buildStakeSignaturePayload
 * @param {Object} request  the modal state: {wallet, tokenId, qty, lockDays}
 * @param {{token: string, tokenId: bigint, amount: bigint, duration: bigint}} args
 * @throws {SupernovaChainError} GUARD_FAILED
 */
export function assertStakePayloadMatches(payload, request, args) {
  const what = "stake";
  assertPayloadEnvelope(payload, request, SUPERNOVA_STAKING_ADDRESS, what);
  assertField(what, "contractFunction", payload.contractFunction,
    "stake(address,uint256,uint256,uint256)");

  /* What the member saw, versus what they picked. */
  const chose = payload.chose || {};
  assertField(what, "tokenId (chosen)", chose.tokenId, request.tokenId);
  assertField(what, "qty (chosen)", chose.qty, request.qty);
  assertField(what, "lockDays (chosen)", chose.lockDays, request.lockDays);

  /* What is actually going on the wire, versus what was described and logged. */
  const described = payload.args || {};
  if (!isSameAddress(args.token, SUPERNOVA_COLLECTION_ADDRESS)) {
    throw guardFailure(
      `${what}: token argument ${String(args.token)} is not the Supernova ` +
        `collection ${SUPERNOVA_COLLECTION_ADDRESS}`
    );
  }
  if (!isSameAddress(described.token, SUPERNOVA_COLLECTION_ADDRESS)) {
    throw guardFailure(
      `${what}: payload token ${String(described.token)} is not the Supernova collection`
    );
  }
  assertField(what, "tokenId (calldata)", asDecimal(args.tokenId), described.tokenId);
  assertField(what, "amount (calldata)", asDecimal(args.amount), described.amount);
  assertField(
    what,
    "durationSeconds (calldata)",
    asDecimal(args.duration),
    described.durationSeconds
  );
}

/**
 * An exit, checked the same way. The one extra thing it holds down is WHICH exit:
 * `unstake` and `emergencyUnstake` take identical calldata shapes, so a flow that
 * told the member "early exit" must not end up signing the other function.
 *
 * @param {Object} payload  from buildUnstakeSignaturePayload
 * @param {Object} request  {wallet, lotId, stakeId?, emergency?}
 * @param {{functionName: string, stakeId: bigint}} args
 * @throws {SupernovaChainError} GUARD_FAILED
 */
export function assertUnstakePayloadMatches(payload, request, args) {
  const what = "unstake";
  assertPayloadEnvelope(payload, request, SUPERNOVA_STAKING_ADDRESS, what);

  const early = Boolean(request.emergency);
  const expectedFunction = early ? "emergencyUnstake" : "unstake";
  assertField(what, "kind", payload.kind, expectedFunction);
  assertField(what, "function", args.functionName, expectedFunction);
  assertField(what, "contractFunction", payload.contractFunction,
    early ? "emergencyUnstake(uint256)" : "unstake(uint256)");

  const chose = payload.chose || {};
  assertField(what, "exit", chose.exit, early ? "early" : "matured");

  const described = payload.args || {};
  assertField(what, "stakeId (calldata)", asDecimal(args.stakeId), described.stakeId);
}

/**
 * The one-time operator approval. There is no member input to cross-check here —
 * the whole transaction is two constants — so the guard is that both of them are
 * exactly the constants, on both the payload and the calldata.
 *
 * @param {Object} payload from buildApprovalSignaturePayload
 * @param {Object} request {wallet}
 * @param {{operator: string, approved: boolean}} args
 * @throws {SupernovaChainError} GUARD_FAILED
 */
export function assertApprovalPayloadMatches(payload, request, args) {
  const what = "setApprovalForAll";
  assertPayloadEnvelope(payload, request, SUPERNOVA_COLLECTION_ADDRESS, what);
  assertField(what, "contractFunction", payload.contractFunction,
    "setApprovalForAll(address,bool)");

  const described = payload.args || {};
  if (!isSameAddress(described.operator, SUPERNOVA_STAKING_ADDRESS)) {
    throw guardFailure(
      `${what}: payload operator ${String(described.operator)} is not the ` +
        `staking contract ${SUPERNOVA_STAKING_ADDRESS}`
    );
  }
  assertField(what, "approved (payload)", described.approved, true);

  /* And the calldata itself, independently of what the payload claimed. */
  assertApprovalOperator(args.operator, args.approved);
}

export { GUARD_MESSAGE };
